import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle } from 'phosphor-react';
import { useVersionCheck } from '../hooks/useVersionCheck';

const cssId = 'update-banner-styles';

const injectStyles = () => {
  if (document.getElementById(cssId)) return;
  const style = document.createElement('style');
  style.id = cssId;
  style.textContent = `
    @keyframes ubRotateBorder {
      to { transform: translate(-50%,-50%) rotate(1turn); }
    }
    @keyframes ubShineSweep {
      0%                { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
      20%               { opacity: .9; }
      45%, 100%         { transform: translateX(120%) skewX(-18deg); opacity: 0; }
    }
    @keyframes ubSparkle {
      0%,100% { opacity:0; transform:scale(.2) rotate(0deg);   filter:blur(1px); }
      18%     { opacity:1; transform:scale(1.35) rotate(35deg); filter:blur(0); }
      36%     { opacity:.35; transform:scale(.75) rotate(75deg); }
      54%     { opacity:1; transform:scale(1.8) rotate(120deg); }
      72%     { opacity:0; transform:scale(.25) rotate(180deg); filter:blur(1.5px); }
    }
    .ub-rotate-el {
      animation: ubRotateBorder 4s linear infinite;
    }
    .ub-shine-el {
      animation: ubShineSweep 3.8s ease-in-out infinite;
    }
    .ub-sparkle-1  { animation: ubSparkle 4s .1s  infinite ease-in-out; }
    .ub-sparkle-2  { animation: ubSparkle 4s .8s  infinite ease-in-out; }
    .ub-sparkle-3  { animation: ubSparkle 4s 1.6s infinite ease-in-out; }
    .ub-sparkle-4  { animation: ubSparkle 4s .4s  infinite ease-in-out; }
    .ub-sparkle-5  { animation: ubSparkle 4s 2.1s infinite ease-in-out; }
    .ub-sparkle-6  { animation: ubSparkle 4s 1.1s infinite ease-in-out; }
    .ub-sparkle-7  { animation: ubSparkle 4s 2.8s infinite ease-in-out; }
    .ub-sparkle-8  { animation: ubSparkle 4s .6s  infinite ease-in-out; }
  `;
  document.head.appendChild(style);
};

const SPARKLES = [
  { left: '8%',  top: '18%', cls: 'ub-sparkle-1' },
  { left: '22%', top: '72%', cls: 'ub-sparkle-2' },
  { left: '38%', top: '30%', cls: 'ub-sparkle-3' },
  { left: '55%', top: '78%', cls: 'ub-sparkle-4' },
  { left: '68%', top: '15%', cls: 'ub-sparkle-5' },
  { left: '80%', top: '58%', cls: 'ub-sparkle-6' },
  { left: '91%', top: '34%', cls: 'ub-sparkle-7' },
  { left: '46%', top: '50%', cls: 'ub-sparkle-8' },
];

const UPDATED_KEY = 'atiende_just_updated';
const UPDATED_TTL_MS = 10_000; // show toast if reloaded within 10s

export const UpdateAvailableBanner: React.FC = () => {
  const { updateAvailable } = useVersionCheck();
  const [showUpdatedToast, setShowUpdatedToast] = React.useState(false);

  React.useEffect(() => {
    injectStyles();
    const check = () => {
      const ts = localStorage.getItem(UPDATED_KEY);
      if (ts && Date.now() - Number(ts) < UPDATED_TTL_MS) {
        localStorage.removeItem(UPDATED_KEY);
        setShowUpdatedToast(true);
        setTimeout(() => setShowUpdatedToast(false), 3500);
      }
    };
    // Check immediately and again after auth flow settles
    check();
    const t = setTimeout(check, 2000);
    return () => clearTimeout(t);
  }, []);

  const reload = () => {
    localStorage.setItem(UPDATED_KEY, String(Date.now()));
    window.location.reload();
  };

  return (
    <>
      <AnimatePresence>
        {showUpdatedToast && (
          <motion.div
            initial={{ opacity: 0, y: 8, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 8, x: '-50%' }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 z-[10001] flex items-center gap-2 px-4 py-2 rounded-full bg-foreground text-background text-sm font-sans shadow-lg pointer-events-none"
            style={{ left: '50%' }}
          >
            <CheckCircle size={16} weight="fill" />
            Actualizado a la última versión de Atiende
          </motion.div>
        )}
      </AnimatePresence>

      {updateAvailable && (
        <div
          role="button"
          onClick={reload}
          style={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 10000,
            borderRadius: 28,
            overflow: 'hidden',
            cursor: 'pointer',
            background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,.18), transparent 24%), radial-gradient(circle at 80% 30%, rgba(25,118,237,.22), transparent 28%), radial-gradient(circle at 50% 90%, rgba(89,204,224,.18), transparent 32%), linear-gradient(135deg, #080812, #111122 45%, #07070d)',
            boxShadow: '0 24px 80px rgba(0,0,0,.55), inset 0 0 80px rgba(255,255,255,.05)',
          }}
        >
          {/* Rotating border */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', overflow: 'hidden', zIndex: 1, pointerEvents: 'none' }}>
            <div
              className="ub-rotate-el"
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                width: 999, height: 999,
                transform: 'translate(-50%,-50%) rotate(0deg)',
                background: 'conic-gradient(transparent, transparent, #1976ed, #59cce0, white, #1976ed, transparent 83%)',
              }}
            />
            <div style={{ position: 'absolute', inset: 3, borderRadius: 25, background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,.12), transparent 25%), linear-gradient(135deg, rgba(8,8,18,.96), rgba(17,17,34,.96))', zIndex: 2 }} />
          </div>

          {/* Shine sweep */}
          <div
            className="ub-shine-el"
            style={{
              position: 'absolute', inset: 0, zIndex: 4,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 25%, rgba(255,255,255,.55) 50%, rgba(255,255,255,0) 75%, transparent 100%)',
              pointerEvents: 'none',
              mixBlendMode: 'screen',
            }}
          />

          {/* Sparkles */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
            {SPARKLES.map((s, i) => (
              <span
                key={i}
                className={s.cls}
                style={{
                  position: 'absolute',
                  left: s.left, top: s.top,
                  width: 7, height: 7,
                  borderRadius: 999,
                  background: 'white',
                  opacity: 0,
                  boxShadow: '0 0 10px white, 0 0 24px rgba(255,255,255,.9), 0 0 44px rgba(89,204,224,.75)',
                }}
              />
            ))}
          </div>

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 5, display: 'flex', alignItems: 'center', gap: 20, padding: '18px 28px' }}>
            <span style={{ color: 'rgba(255,255,255,.9)', fontSize: 16, fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
              Nueva versión disponible
            </span>
            <button
              type="button"
              onClick={reload}
              style={{
                borderRadius: 10,
                background: 'white',
                border: 'none',
                padding: '7px 20px',
                fontSize: 14,
                fontWeight: 600,
                color: '#09090f',
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              Recargar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateAvailableBanner;
